import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { BRAND } from '@/config/brand';

/**
 * Logo BD Innovation.
 *
 * Le logo est dessiné pour un fond clair : le mot « INNOVATION » est noir. On
 * ne le pose donc jamais sur du sombre — la moitié du dessin y disparaîtrait.
 * L'application ayant un fond blanc, il se pose directement, sans plaque : la
 * plaque noire de l'ancien logo n'a plus de raison d'être et l'alourdirait.
 *
 * Deux fichiers, deux usages :
 * - `logo-bd-mark.png` : le losange seul, pour les barres et les petits formats.
 * - `logo-bd.png`      : le verrouillage complet, nom et signature compris.
 *
 * Pour changer de fichier, remplacez ces deux images dans `public/` : aucun
 * écran n'a besoin d'être touché.
 */
export function DbMark({ className }: { className?: string }) {
  return (
    <img
      src="/logo-bd-mark.png"
      alt={BRAND.name}
      className={className}
      width={261}
      height={264}
      decoding="async"
    />
  );
}

/** Verrouillage complet : losange, nom et signature. */
export function DbLockup({ className }: { className?: string }) {
  return (
    <img
      src="/logo-bd.png"
      alt={`${BRAND.name} — ${BRAND.tagline}`}
      className={className}
      width={696}
      height={265}
      decoding="async"
    />
  );
}

/**
 * Verrou de marque des barres de navigation.
 *
 * Le losange seul, et le nom composé en HTML à côté. Le verrouillage complet
 * n'est pas utilisable ici : à 40 px de haut, « AUTO CARS » et la signature
 * tombent sous 6 px et deviennent une tache. Le texte composé, lui, reste net
 * à toutes les tailles et sur tous les écrans.
 */
export function Logo({
  className,
  to = '/',
  withTagline = false,
}: {
  className?: string;
  to?: string;
  /** Affiche le nom et la signature a cote du losange. */
  withTagline?: boolean;
}) {
  return (
    <Link
      to={to}
      className={cn('inline-flex shrink-0 items-center gap-2.5', className)}
      aria-label={BRAND.name}
    >
      <DbMark className="h-10 w-auto" />
      {/* Le nom s'affiche aussi sur téléphone. Il était masqué sous 640 px, si
          bien que le logo seul ne disait plus de quel service il s'agissait —
          un visiteur qui arrive par un lien partagé ne voyait qu'un losange.
          Il est simplement composé un cran plus petit pour tenir à côté de la
          cloche et du menu. */}
      {withTagline && (
        <span className="leading-tight">
          <span className="block text-[13px] font-extrabold tracking-tight text-ink-950 sm:text-[15px]">
            BD <span className="font-semibold text-ink-700">INNOVATION</span>
          </span>
          <span className="block text-[8px] font-bold uppercase tracking-[0.12em] text-brand-600 sm:text-[9px] sm:tracking-[0.14em]">
            Auto Cars
          </span>
        </span>
      )}
    </Link>
  );
}
